import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mic, Square, Play, RotateCcw, Pause } from 'lucide-react';
import { ARCHETYPES } from './archetypeData';
import sonicCues from '../Premium/sonicCues';

const RecordState = ({ archetypeId, musicTrack, onComplete, onBack }) => {
  const archetype = ARCHETYPES[archetypeId];
  const affirmations = archetype?.affirmations || [];

  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const playbackRef = useRef(null);
  const affirmationTimerRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // Time per affirmation (6 seconds each — enough time to read with feeling)
  const TIME_PER_AFFIRMATION = 6000;

  // Auto-advance affirmations while recording
  useEffect(() => {
    if (isRecording && currentAffirmationIndex < affirmations.length - 1) {
      affirmationTimerRef.current = setTimeout(() => {
        sonicCues.tap();
        setCurrentAffirmationIndex((prev) => prev + 1);
      }, TIME_PER_AFFIRMATION);

      return () => {
        if (affirmationTimerRef.current) {
          clearTimeout(affirmationTimerRef.current);
        }
      };
    }
  }, [isRecording, currentAffirmationIndex, affirmations.length]);

  // Track recording time
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      return () => {
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
      };
    }
  }, [isRecording]);

  // Auto-stop recording after all affirmations + a few seconds
  useEffect(() => {
    if (isRecording && currentAffirmationIndex === affirmations.length - 1) {
      const stopTimer = setTimeout(() => {
        stopRecording();
      }, TIME_PER_AFFIRMATION);

      return () => clearTimeout(stopTimer);
    }
  }, [isRecording, currentAffirmationIndex, affirmations.length]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedBlob(audioBlob);
        setRecordedUrl(audioUrl);
        stream.getTracks().forEach((track) => track.stop());
        sonicCues.bloom();
      };

      sonicCues.threshold();
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      setCurrentAffirmationIndex(0);
    } catch (err) {
      console.error('Recording error:', err);
      alert('Please allow microphone access to record your affirmations.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (affirmationTimerRef.current) {
        clearTimeout(affirmationTimerRef.current);
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const playRecording = () => {
    const el = playbackRef.current;
    if (el && recordedUrl) {
      if (isPlaying) {
        el.pause();
        setIsPlaying(false);
      } else {
        sonicCues.tap();
        el.src = recordedUrl;
        el.play().catch((err) => {
          console.error('Playback error:', err);
        });
        setIsPlaying(true);
        el.onended = () => setIsPlaying(false);
      }
    }
  };

  const reRecord = () => {
    sonicCues.tap();
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setCurrentAffirmationIndex(0);
    setRecordingTime(0);
  };

  const handleContinue = () => {
    sonicCues.bloom();
    onComplete(recordedBlob);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, #18213A 0%, #0a1224 50%, #03060A 100%)",
      }}
      data-testid="record-state"
    >
      <audio ref={playbackRef} />

      {/* Header with recording state */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="text-center pt-10 pb-4 px-8"
      >
        <p
          className="uppercase tracking-[0.32em] mb-3"
          style={{
            color: "#D7BB7A",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
          }}
        >
          Threshold · 4 of 5
        </p>
        {!recordedUrl ? (
          <>
            <h1
              className="text-2xl font-light italic mb-1"
              style={{
                color: "#F3EDDD",
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                letterSpacing: "0.04em",
              }}
            >
              {isRecording ? "Speak it into being" : "When you are ready"}
            </h1>
            <p
              className="italic"
              style={{
                color: "rgba(243, 237, 221, 0.55)",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.95rem",
              }}
            >
              {isRecording ? "Read each affirmation with feeling." : "Press record below."}
            </p>
            {isRecording && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 font-mono"
                style={{
                  color: "#FF4D29",
                  fontSize: "1.05rem",
                  letterSpacing: "0.12em",
                }}
              >
                {formatTime(recordingTime)}
              </motion.p>
            )}
          </>
        ) : (
          <>
            <h1
              className="text-2xl font-light italic mb-1"
              style={{
                color: "#F3EDDD",
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                letterSpacing: "0.04em",
              }}
            >
              Listen back
            </h1>
            <p
              className="italic"
              style={{
                color: "rgba(243, 237, 221, 0.55)",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.95rem",
              }}
            >
              Re-record if needed, or continue.
            </p>
          </>
        )}
      </motion.div>

      {/* All affirmations displayed with current one highlighted */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 overflow-y-auto py-4">
        <div className="space-y-4 w-full max-w-md">
          {affirmations.map((affirmation, index) => {
            const isCurrent = index === currentAffirmationIndex;
            const isPast = index < currentAffirmationIndex;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0.3 }}
                animate={{
                  opacity: isCurrent ? 1 : isPast ? 0.45 : 0.25,
                  scale: isCurrent ? 1.05 : 1,
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`text-center transition-all ${isCurrent ? 'py-3' : 'py-1'}`}
              >
                <p
                  className="font-light leading-relaxed italic transition-all duration-500"
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: isCurrent ? "1.35rem" : "1.05rem",
                    color: isCurrent
                      ? "#F3EDDD"
                      : isPast
                      ? "rgba(243, 237, 221, 0.42)"
                      : "rgba(243, 237, 221, 0.25)",
                  }}
                >
                  {affirmation}
                </p>

                {/* Progress indicator for current */}
                {isCurrent && isRecording && (
                  <div className="w-24 h-[2px] mx-auto mt-3 rounded-full overflow-hidden" style={{ background: "rgba(215, 187, 122, 0.12)" }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg, #D7BB7A 0%, #A88A4C 100%)" }}
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: TIME_PER_AFFIRMATION / 1000, ease: "linear" }}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Controls — Premium gilded surface */}
      <div
        className="flex-shrink-0 p-6 pb-28 space-y-4 relative z-10"
        style={{
          background:
            "linear-gradient(to top, #03060A 0%, rgba(3, 6, 10, 0.85) 60%, transparent 100%)",
        }}
      >
        {!recordedUrl ? (
          // Recording mode — single gilded record orb
          <div className="flex flex-col items-center gap-3">
            <motion.button
              whileTap={{ scale: 0.92 }}
              whileHover={{ scale: 1.04 }}
              onClick={isRecording ? stopRecording : startRecording}
              className="relative w-20 h-20 rounded-full flex items-center justify-center transition-all"
              style={{
                background: isRecording
                  ? "radial-gradient(circle at 30% 30%, #FF6B47 0%, #C8341A 100%)"
                  : "radial-gradient(circle at 30% 30%, #E7CB8B 0%, #A88A4C 80%)",
                border: isRecording
                  ? "1px solid rgba(255, 200, 180, 0.45)"
                  : "1px solid rgba(215, 187, 122, 0.55)",
                boxShadow: isRecording
                  ? "0 0 40px rgba(255, 77, 41, 0.45), 0 0 0 1px rgba(255, 200, 180, 0.35) inset"
                  : "0 0 32px rgba(215, 187, 122, 0.32), 0 0 0 1px rgba(215, 187, 122, 0.4) inset",
              }}
              data-testid="record-btn"
            >
              {isRecording ? (
                <Square size={24} style={{ color: "#FFF" }} fill="#FFF" />
              ) : (
                <Mic size={26} style={{ color: "#070D0B" }} />
              )}
              {isRecording && (
                <motion.span
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{ border: "1px solid rgba(255, 200, 180, 0.55)" }}
                  animate={{ scale: [1, 1.25], opacity: [0.6, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                />
              )}
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="uppercase"
              style={{
                color: "rgba(243, 237, 221, 0.45)",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.56rem",
                letterSpacing: "0.28em",
              }}
            >
              {isRecording ? "Tap to stop when finished" : "Tap to record"}
            </motion.p>
          </div>
        ) : (
          // Playback mode — gilded chip cluster
          <div className="space-y-4">
            <div className="flex justify-center gap-3">
              <button
                onClick={playRecording}
                className="rec-chip"
                data-active={isPlaying ? "true" : "false"}
                data-testid="record-play-btn"
                style={{ padding: "0.7rem 1rem" }}
                aria-label={isPlaying ? "Pause" : "Play recording"}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={reRecord}
                className="rec-chip"
                data-testid="record-rerecord-btn"
                style={{ padding: "0.7rem 1rem" }}
                aria-label="Re-record"
              >
                <RotateCcw size={14} />
                Re-record
              </button>
            </div>

            <div className="flex justify-center">
              <motion.button
                whileHover={{ y: -2 }}
                onClick={handleContinue}
                className="rec-chip"
                data-active="true"
                data-testid="record-continue-btn"
                style={{ padding: "0.85rem 2rem", fontSize: "0.66rem" }}
              >
                Continue →
              </motion.button>
            </div>
          </div>
        )}

        {/* Subtle accent glow at bottom, archetype-tinted */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background: `linear-gradient(to top, ${archetype?.glowColor || 'rgba(215, 187, 122, 0.18)'} 0%, transparent 100%)`,
          }}
          animate={{ opacity: [0.25, 0.45, 0.25] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </div>
    </div>
  );
};

export default RecordState;
